/**
 * Global pointer-vs-keyboard modality flag, stamped on `<html>` as
 * `data-input-modality`.
 *
 * CSS cannot decide this on its own for a text field. The `:focus-visible`
 * heuristic always matches a focused text-entry element regardless of how it
 * was focused, so `focus-visible:ring-*` on an `<input>` still rings on a mouse
 * click — the thing `.claude/rules/design-system.md` → Focus rings forbids.
 * Component-level detection already exists for hand-wired fields
 * (`useKeyboardFocus`, `useTextFieldFocusRing`), but the vendored Untitled UI
 * `base/input` raises its ring from React Aria's modality-blind `isFocusWithin`
 * and MUST NOT be edited. A root-level flag lets one stylesheet rule fold that
 * ring back to its resting state without touching vendored code.
 *
 * Only focus-movement keys arm keyboard mode. Ordinary typing must not, or a
 * field the user clicked into would ring on its first character.
 */

const MODALITY_ATTRIBUTE = 'data-input-modality';

/** Keys that move focus. Everything else — including every character key —
 *  leaves the current modality alone. */
const FOCUS_MOVEMENT_KEYS = new Set([
  'Tab',
  'Escape',
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'Home',
  'End',
  'PageUp',
  'PageDown',
]);

export type InputModality = 'keyboard' | 'pointer';

export function setInputModality(modality: InputModality): void {
  document.documentElement.setAttribute(MODALITY_ATTRIBUTE, modality);
}

export function getInputModality(): InputModality {
  return document.documentElement.getAttribute(MODALITY_ATTRIBUTE) === 'keyboard'
    ? 'keyboard'
    : 'pointer';
}

/**
 * Starts tracking and returns a teardown. Listeners are registered in the
 * capture phase so a handler that stops propagation cannot strand the flag on a
 * stale modality.
 */
export function initInputModality(): () => void {
  const onPointerDown = () => setInputModality('pointer');

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.metaKey || event.altKey || event.ctrlKey) {
      return;
    }
    if (FOCUS_MOVEMENT_KEYS.has(event.key)) {
      setInputModality('keyboard');
    }
  };

  setInputModality('pointer');
  document.addEventListener('pointerdown', onPointerDown, true);
  document.addEventListener('keydown', onKeyDown, true);

  return () => {
    document.removeEventListener('pointerdown', onPointerDown, true);
    document.removeEventListener('keydown', onKeyDown, true);
  };
}
